import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormHelperText,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  getAllDefaultConversationTemplates,
  getConversationTemplates,
  normalizeTouchpoints,
} from '@healthdesk/shared-services';
import TouchpointBadge from './TouchpointBadge';

const TAB_MY = 'my';
const TAB_SYSTEM = 'system';
const DEFAULT_TOUCHPOINT = 'securelink';

function isSameRef(left, right) {
  if (!left || !right) return false;
  return (
    left.source === right.source &&
    left.templateId === right.templateId &&
    (left.adminId || '') === (right.adminId || '')
  );
}

const ConversationTemplatePicker = ({
  value,
  onChange,
  touchpoint = DEFAULT_TOUCHPOINT,
  adminId,
  label = 'Conversation template',
  helperText = '',
  disabled = false,
}) => {
  const [tab, setTab] = useState(TAB_MY);
  const [myTemplates, setMyTemplates] = useState([]);
  const [loadingMy, setLoadingMy] = useState(false);
  const [loadError, setLoadError] = useState('');

  const systemTemplates = useMemo(() => {
    return getAllDefaultConversationTemplates().filter(row =>
      normalizeTouchpoints(row).includes(touchpoint)
    );
  }, [touchpoint]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!adminId) {
        setMyTemplates([]);
        setLoadError('');
        return;
      }
      setLoadingMy(true);
      setLoadError('');
      try {
        const rows = await getConversationTemplates(adminId, {
          touchpoint,
          status: 'active',
        });
        if (!active) return;
        setMyTemplates(rows);
      } catch (error) {
        if (!active) return;
        console.warn('[ConversationTemplatePicker] Failed to load templates', error);
        setLoadError('Could not load your active templates.');
        setMyTemplates([]);
      } finally {
        if (active) setLoadingMy(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [adminId, touchpoint]);

  const openNewTemplate = () => {
    window.open('/dashboard/conversation-templates/new', '_blank', 'noopener,noreferrer');
  };

  const renderTemplateRow = (row, source, rowAdminId = undefined) => {
    const ref = {
      source,
      templateId: row.templateId || row.id,
      ...(rowAdminId ? { adminId: rowAdminId } : {}),
    };
    const selected = isSameRef(value, ref);
    return (
      <Paper
        key={`${source}:${row.id || row.templateId}`}
        variant="outlined"
        sx={{
          p: 1.5,
          borderColor: selected ? 'primary.main' : 'divider',
          bgcolor: selected ? 'primary.50' : 'background.paper',
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2">{row.title || row.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {row.specialty || 'general'}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {normalizeTouchpoints(row).map(tp => (
                <TouchpointBadge key={`${row.id || row.templateId}-${tp}`} value={tp} />
              ))}
              <Chip
                size="small"
                variant="outlined"
                label={source === 'custom' ? 'Custom' : 'System'}
              />
            </Box>
          </Box>
          <Button
            size="small"
            variant={selected ? 'contained' : 'outlined'}
            onClick={() => onChange(ref)}
            disabled={disabled}
          >
            {selected ? 'Selected' : 'Use'}
          </Button>
        </Stack>
      </Paper>
    );
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Tabs value={tab} onChange={(_, next) => setTab(next)} sx={{ mb: 1 }}>
        <Tab value={TAB_MY} label={`My templates (${myTemplates.length})`} />
        <Tab value={TAB_SYSTEM} label={`System templates (${systemTemplates.length})`} />
      </Tabs>

      {tab === TAB_MY && (
        <Stack spacing={1}>
          {!adminId && (
            <Alert severity="info">
              Link a Patient Encounter Template first to load this clinic&apos;s admin templates.
            </Alert>
          )}
          {loadingMy && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {!loadingMy && loadError && <Alert severity="error">{loadError}</Alert>}
          {!loadingMy && !loadError && adminId && myTemplates.length === 0 && (
            <Alert severity="info">No active custom templates for this touchpoint yet.</Alert>
          )}
          {!loadingMy &&
            !loadError &&
            myTemplates.map(row => renderTemplateRow(row, 'custom', adminId))}
        </Stack>
      )}

      {tab === TAB_SYSTEM && (
        <Stack spacing={1}>
          {systemTemplates.map(row => renderTemplateRow(row, 'system'))}
        </Stack>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <Button size="small" onClick={() => onChange(null)} disabled={disabled || !value}>
          Clear
        </Button>
        <Button size="small" startIcon={<OpenInNewIcon />} onClick={openNewTemplate}>
          Create new template →
        </Button>
      </Stack>

      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </Box>
  );
};

export default ConversationTemplatePicker;

